/**
 * DealPhaseTrail - Trilha de fases do pipeline (estilo Bitrix) no modal de Negócio
 *
 * A partir da v1.1.0 este componente é apenas um adaptador: converte as fases
 * do pipeline para o formato genérico e delega a renderização ao componente
 * compartilhado `PhaseTrail`, reaproveitado também por Tarefas/Retenção/Jurídico.
 *
 * @version 1.1.0
 * RELEASE: 2026-09-03 v1.1.0 - Passa a usar o componente compartilhado PhaseTrail
 *          (sem mudança visual).
 * RELEASE: 2026-09-03 v1.0.0 - Criação: trilha de fases clicável (direção "Pipeline glass").
 */

import React from 'react';
import { PhaseTrail } from '@/components/shared/PhaseTrail';
import type { PhaseStats } from '@/hooks/usePipelinePhaseStats';

/** Fase exibida na trilha (subconjunto do que o pipeline devolve) */
export interface DealPhaseTrailItem {
  phase_id: string;
  phase_name: string;
  phase_color: string | null;
  phase_sort_order: number;
  is_won: boolean;
  is_lost: boolean;
}

interface DealPhaseTrailProps {
  /** Fases do pipeline (já filtradas por visibilidade) */
  phases: DealPhaseTrailItem[] | PhaseStats[];
  /** Fase atual do negócio */
  currentPhaseId: string | null | undefined;
  /** Retorna se o usuário pode mover o negócio PARA a fase informada */
  canMoveToPhase: (phaseId: string) => boolean;
  /** Motivo do bloqueio (tooltip) quando a fase não é clicável */
  blockedReason?: (phaseId: string) => string | null;
  /** Move o negócio para a fase clicada */
  onSelectPhase: (phaseId: string) => void;
  /** Trilha somente leitura (negócio ganho/perdido ou mutação em andamento) */
  readOnly?: boolean;
  className?: string;
}

export function DealPhaseTrail({
  phases,
  currentPhaseId,
  canMoveToPhase,
  blockedReason,
  onSelectPhase,
  readOnly = false,
  className,
}: DealPhaseTrailProps) {
  // Converte as fases do pipeline para o contrato genérico da trilha
  const itens = React.useMemo(
    () =>
      (phases ?? []).map((f) => ({
        id: f.phase_id,
        name: f.phase_name,
        color: f.phase_color ?? null,
        sortOrder: f.phase_sort_order,
      })),
    [phases],
  );

  return (
    <PhaseTrail
      phases={itens}
      currentPhaseId={currentPhaseId}
      canMoveToPhase={canMoveToPhase}
      blockedReason={blockedReason}
      onSelectPhase={onSelectPhase}
      readOnly={readOnly}
      className={className}
    />
  );
}
